import { env } from "./env.js";

/**
 * Make relative `/api/...` requests work inside the desktop app.
 *
 * In a browser the SPA is served same-origin with the API, so a relative
 * `/api/...` URL resolves to the dashboard host and everything works. In
 * the **desktop** app the SPA is loaded from the bundle
 * (`tauri://localhost` on macOS/Linux, `http://tauri.localhost` on
 * Windows), so a relative `/api/...` resolves to the app's OWN origin —
 * there's no server there, so the call dies with "failed to fetch" /
 * "could not bootstrap CRM".
 *
 * The main `lib/api.ts` wrapper already prefixes `env.apiUrl`, but many
 * modules use a raw `fetch('/api/...')` (CRM bootstrap, Otto, AI generate,
 * ai-providers, admin security, …). Rather than fix each call site (and
 * re-break on the next one added), patch `window.fetch` ONCE so every
 * relative `/api` request hits the real server.
 *
 * No-op in the browser: `env.apiUrl` is "" there, so this returns early and
 * relative URLs stay same-origin. Only the desktop build sets an absolute
 * `VITE_DASHBOARD_API_URL`. (axios — used by the CRM client — bypasses
 * window.fetch, so the CRM axios instance sets its own `baseURL: env.apiUrl`
 * separately.)
 */
export function installDesktopApiBase(base: string = env.apiUrl): void {
  if (!base) return; // browser / same-origin → nothing to do
  if (typeof window === "undefined" || typeof window.fetch !== "function") return;
  const w = window as Window & { __xopApiBaseInstalled?: boolean };
  if (w.__xopApiBaseInstalled) return; // idempotent
  w.__xopApiBaseInstalled = true;

  const original = window.fetch.bind(window);
  const isRelativeApi = (u: string): boolean => u === "/api" || u.startsWith("/api/");

  window.fetch = ((input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    if (typeof input === "string" && isRelativeApi(input)) {
      return original(base + input, init);
    }
    if (input instanceof URL && isRelativeApi(input.pathname) && input.origin !== base) {
      // tauri://localhost/api/... → absolute server URL
      return original(base + input.pathname + input.search, init);
    }
    if (input instanceof Request && input.url) {
      // Request URLs are already absolutized against the document origin
      // (tauri://localhost/api/...). Re-point only those at the server.
      try {
        const parsed = new URL(input.url);
        if (isRelativeApi(parsed.pathname) && parsed.origin !== base) {
          return original(new Request(base + parsed.pathname + parsed.search, input));
        }
      } catch {
        /* fall through */
      }
    }
    return original(input, init);
  }) as typeof window.fetch;
}
